import React, { useEffect, useMemo, useState } from 'react';
import { useTable, usePagination } from 'react-table';
import { DOCUMENTS } from '../../constants/columns';

// components
import EmptyData from '../../components/EmptyData/EmptyData';
import icon from '../../brand/generated/buttonIcons/addDeal.svg';
import getDealDocuments from '../../api/deals/getDealDocuments';
import { useParams } from 'react-router-dom';
import HookForm from '../../components/HookForm/HookForm';
import SelectDocumentType from '../../components/Selects/SelectDocumentType';
import AddButton from '../../components/AddButton/AddButton';
import { textStyles } from '../../constants/textStyles';
import DeleteButton from '../../components/DeleteButton/DeleteButton';
import Button from '../../components/Button/Button';
import DocsInputNew from '../../components/Inputs/DocsInput/DocsInputNew';
import Loader from '../../components/Loader/Loader';
import LoaderInline from '../../components/Loader/LoaderInline';
import UploadProgressBar from '../../components/UploadProgressBar';
import useDocumentUpload from '../../hooks/useDocumentUpload';
import HalykGetGuaranteeLetter from './DealCreate/HalykFlow/HalykGetGuaranteeLetter';
import getDealCard from '../../api/deals/getDealCard';
import getCreditAppeal from '../../api/deals/getCreditAppeal';
import {
  BANKS,
  DEAL_STEPS,
  DOCUMENT_TYPES_GUARANTEE_LETTER,
  DOCUMENT_TYPES_PAYMENT_ORDER,
  DOCUMENT_TYPES_SPOUSE_CONSENT,
} from '../../constants';
import sendMarriedDoc from '../../api/creditPlatform/sendMarriedDoc';
import alert from '../../helpers/alert';
import CheckBankStatus from './DealCreate/CommonBankSteps/CheckBankStatus';
import cancelCreditAppeal from '../../api/creditPlatform/forte/cancelCreditAppeal';
import extractErrorMessage from '../../utils/extractErrorMessage';
import { BANK_CREDIT_WAITING } from '../../constants/bankCreditStatuses';
import processDocument from '../../api/bank/processDocument';
import ProcessDocumentButton from '../../components/ProcessDocumentButton/ProcessDocumentButton';
import StepControlButtons from './DealCreate/CommonBankSteps/StepControlButtons';

const formStyles = {
  document: { display: 'grid', gap: '20px', gridTemplateColumns: `1fr 1fr 500px`, alignItems: 'flex-end' },
};

const columns = DOCUMENTS;

// Банки, которым бэк умеет переслать согласие супруга (sendMarriedDoc).
const SPOUSE_CONSENT_BANKS = [BANKS.HALYK, BANKS.FREEDOM, BANKS.SHINHAN, BANKS.EURAZ, BANKS.BCK];

const DealDocs = () => {
  const row = () => [
    {
      component: SelectDocumentType,
      name: 'fileTypeId',
    },
    {
      component: DocsInputNew,
      name: 'file',
      label: 'Файл документа',
      text: 'Файл документа',
      id: Math.random(),
    },
    {
      component: DeleteButton,
      name: 'delete',
      onClick: (e, index) => {
        removeRow(e, index);
      },
    },
  ];
  const { id } = useParams();
  const [form, setForm] = useState([row()]);
  const [loading, setLoading] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);
  const [documents, setDocuments] = React.useState([]);
  const { uploadMultiple, uploading, progress, currentFileName, uploadQueue } = useDocumentUpload();
  const table = useTable(
    {
      columns,
      data: documents,
      //TODO: make pagination here
      initialState: { pageIndex: 0, pageSize: 1000000 },
    },
    usePagination
  );

  const init = async () => {
    setLoadingPage(true);
    let array = [];
    await getDealDocuments({ id: id })
      .then((res) => {
        // setDocument([...res.data]);
        res.data.forEach((item) => {
          array.push({
            id: item.id,
            url: item.url,
            type: item.type,
          });
        });
      })
      .finally(() => setLoadingPage(false));
    setDocuments([...array]);
    setForm([row()]);
  };

  React.useEffect(() => {
    init();
  }, []);

  const addRow = () => {
    setForm([...form, row()]);
  };

  const removeRow = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    const newForm = form;
    newForm.slice(index, 1);
    setForm(newForm);
  };

  const onSubmit = async () => {
    const fields = form.map((i) => i.map((i) => i.value?.value || i.value));
    const filesToUpload = fields
      .map((field) => ({
        dealId: id,
        fileTypeId: field[0],
        file: field[1],
      }))
      .filter((item) => item.file);

    if (!filesToUpload.length) {
      return;
    }

    try {
      await uploadMultiple(filesToUpload);
      setLoading(true);
      await sendSpouseConsentToBank(filesToUpload);
      await init();
    } finally {
      setLoading(false);
    }
  };

  // Согласие супруга, загруженное с этой вкладки, раньше только сохранялось в
  // сделке: в банк оно уходит лишь из шага «Нотариальные документы» кредитного
  // потока. Менеджер грузил его здесь четыре раза (сделка 192702), а Халык так
  // ничего и не получил. Теперь после загрузки шлём его выбранному банку тем же
  // вызовом, что и шаг потока.
  const sendSpouseConsentToBank = async (uploaded) => {
    const hasConsent = uploaded.some((item) => Number(item.fileTypeId) === DOCUMENT_TYPES_SPOUSE_CONSENT);
    if (!hasConsent) return;
    if (!chosenBankAppealId || !SPOUSE_CONSENT_BANKS.includes(chosenBankId)) {
      alert.error('Согласие сохранено в сделке, но в банк не отправлено: банк не выбран или не принимает его через брокер');
      return;
    }
    try {
      await sendMarriedDoc({ appealId: chosenBankAppealId });
      alert.success('Согласие супруга отправлено в банк');
    } catch (err) {
      alert.error(
        `Согласие сохранено в сделке, но банк его не принял: ${extractErrorMessage(err) || 'ошибка отправки'}`,
        { autoClose: false, closeOnClick: false }
      );
    }
  };

  const [chosenBankId, setChosenBankId] = useState(null);
  const [chosenBankAppealId, setChosenBankAppealId] = useState(null);
  const [chunkId, setChunkId] = useState(null);
  const [creditAppeal, setCreditAppeal] = useState(null);

  useEffect(() => {
    getDealCard({ id }).then((res) => {
      setChunkId(res?.data?.chunkId);
      getCreditAppeal({ chunkId: res?.data?.chunkId }).then((res) => {
        const chosenBank = res?.data?.appeals?.find((appeal) => appeal?.selected);
        setChosenBankId(chosenBank?.bank?.id);
        setChosenBankAppealId(chosenBank?.id);
        setCreditAppeal(chosenBank);
      });
    });
  }, [id]);

  const showHalykGetGuaranteeLetterButton = useMemo(() => {
    return chosenBankId === BANKS.HALYK;
  }, [chosenBankId, documents]);

  const isForteChosen = useMemo(() => {
    return chosenBankId === BANKS.FORTE;
  }, [chosenBankId]);
  const isEurazChosen = useMemo(() => {
    return chosenBankId === BANKS.EURAZ;
  }, [chosenBankId]);

  const showForteUpdateButton = useMemo(() => {
    const isGuaranteeLetterPresent = documents?.some((item) => item?.typeId === DOCUMENT_TYPES_PAYMENT_ORDER);
    return chosenBankId === BANKS.FORTE && !isGuaranteeLetterPresent;
  }, [chosenBankId, documents]);

  const lastBankStep = useMemo(() => {
    if(!chosenBankId) return null;
    const bankKeys = Object.keys(BANKS);
    let bankName;

    for(let i of bankKeys) {
        if(BANKS?.[i] == chosenBankId) {
            bankName = i;
        }
    }

    if(!bankName || !DEAL_STEPS?.[bankName]) return null;
    
    const bankSteps = DEAL_STEPS[bankName];
    if(typeof bankSteps !== 'object' || Array.isArray(bankSteps)) return null;
    
    const stepsValues = Object.values(bankSteps);
    if(!stepsValues || stepsValues.length < 2) return null;
    
    return stepsValues[stepsValues.length - 2];
  }, [chosenBankId]);

  const cancelForteAppeal = () => {
    cancelCreditAppeal(chosenBankAppealId)
      .then(() => {
        init();
        alert.success('Заявка успешно отменена');
      })
      .catch((err) => {
        alert.error(extractErrorMessage(err) || 'Ошибка при отмене заявки');
      });
  };

  if (uploading) {
    return <UploadProgressBar progress={progress} fileName={currentFileName} queue={uploadQueue} />;
  }

  if (loading) {
    return (
      <div style={{ padding: '10px 20px', backgroundColor: 'white', marginTop: '20px', border: '1px solid #E0E0E0' }}>
        <LoaderInline />
      </div>
    );
  }

  return (
    <>
      {loadingPage ? (
        <Loader />
      ) : (
        <div style={{ padding: '10px 20px', backgroundColor: 'white', marginTop: '20px', border: '1px solid #E0E0E0' }}>
          
          <StepControlButtons
            dealId={id}
            prevStep={lastBankStep}
            update={() => window.location.reload()}
            setLoading={setLoading}
          />
          <EmptyData
            title={`Документы к сделке (${documents.length})`}
            subtitle={'К этой сделке не найдено ни одного документа'}
            action={null}
            icon={icon}
            table={table}
            data={documents}
          />

          <div
            style={{
              display: 'flex',
              gap: 20,
            }}
          >
            {showHalykGetGuaranteeLetterButton && (
              <HalykGetGuaranteeLetter appealId={chosenBankAppealId} onSuccess={init} />
            )}
            {showForteUpdateButton && (
              <CheckBankStatus
                setLoading={setLoading}
                chunkId={chunkId}
                bankId={chosenBankId}
                onSuccess={init}
                text="Платежное поручение"
              />
            )}
            {isForteChosen && (
              <Button onClick={cancelForteAppeal} fill>
                Отменить заявку в ForteBank
              </Button>
            )}
            {isEurazChosen && creditAppeal?.statusId == BANK_CREDIT_WAITING ? (
              <ProcessDocumentButton chunkId={chunkId} />
            ) : null}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', columnGap: '20px', marginTop: 20 }}>
            <p style={textStyles.formTitle}>Документы сделки</p>
          </div>
          {form.map((row, index) => (
            <HookForm grid={formStyles.document} inputs={row} style={{ margin: 0 }} index={index} />
          ))}

          <AddButton text="Добавить еще документ" onClick={addRow} />

          <div
            style={{
              marginTop: 20,
            }}
          >
            <Button text="Сохранить" fill onClick={onSubmit} />
          </div>
        </div>
      )}
    </>
  );
};

export default DealDocs;
